import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Bot, Loader2, Eye, EyeOff, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) return setError("Password must be at least 6 characters.");
    if (password !== confirm) return setError("Passwords don't match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated — you can log in now.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0B0F17] text-slate-200 p-6">
      <div className="absolute inset-0 grid-bg opacity-40" />
      <div className="relative z-10 w-full max-w-md">
        <Link to="/" className="flex items-center gap-2.5 justify-center mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center">
            <Bot className="w-5 h-5 text-[#0B0F17]" />
          </div>
          <span className="font-display font-extrabold text-xl text-white">DVEPL</span>
        </Link>

        <div className="glass rounded-2xl p-6 sm:p-8 border border-slate-800/70">
          {!token ? (
            <div className="text-center" data-testid="reset-invalid">
              <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto" />
              <h1 className="font-display text-xl font-bold text-white mt-3">Invalid reset link</h1>
              <p className="text-sm text-slate-400 mt-2">This link is missing its token. Please request a new reset email.</p>
              <Button data-testid="reset-back-login" onClick={() => navigate("/login")}
                className="mt-5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-[#0B0F17] font-semibold">Back to login</Button>
            </div>
          ) : done ? (
            <div className="text-center" data-testid="reset-success">
              <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto" />
              <h1 className="font-display text-xl font-bold text-white mt-3">Password updated</h1>
              <p className="text-sm text-slate-400 mt-2">Your password has been changed. Log in with your new password.</p>
              <Button data-testid="reset-go-login" onClick={() => navigate("/login")}
                className="mt-5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-[#0B0F17] font-semibold">Go to login</Button>
            </div>
          ) : (
            <>
              <h1 className="font-display text-2xl font-bold text-white">Set a new password</h1>
              <p className="mt-2 text-slate-400 text-sm">Choose a new password for your account.</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <Label className="text-slate-300">New password</Label>
                  <div className="relative mt-1.5">
                    <Input data-testid="reset-password-input" type={showPw ? "text" : "password"} value={password}
                      onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••"
                      className="h-11 pr-11 bg-slate-900/60 border-slate-700 focus-visible:ring-emerald-500" />
                    <button type="button" data-testid="reset-toggle-visibility" onClick={() => setShowPw((v) => !v)}
                      aria-label={showPw ? "Hide password" : "Show password"}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-emerald-300">
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300">Confirm password</Label>
                  <Input data-testid="reset-confirm-input" type={showPw ? "text" : "password"} value={confirm}
                    onChange={(e) => setConfirm(e.target.value)} required placeholder="••••••••"
                    className="mt-1.5 h-11 bg-slate-900/60 border-slate-700 focus-visible:ring-emerald-500" />
                </div>
                {error && (
                  <p data-testid="reset-error" className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2">
                    {error}
                  </p>
                )}
                <Button type="submit" data-testid="reset-submit-btn" disabled={loading}
                  className="w-full h-11 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-[#0B0F17] font-semibold">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
                </Button>
              </form>
              <p className="mt-6 text-sm text-slate-400 text-center">
                <Link to="/login" className="text-emerald-400 font-medium hover:underline">Back to login</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
